import { Controller, IDynamicLoader, Loader, MainMenu, Menu, Toolbar } from '@tencent/workbench';
import { FIXED_KEYS_DOUBLE } from '@src/framework/workbench/schema/constant';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ShapeTabFeature } from './shape-tab-feature';
import { checkChildModelHasSomeTypeElement } from '@src/framework/frame/components/tool-bar/common/compare-property';
import { fontBorderToolbar, fontEffectToolbar, fontGlowToolbar, fontReflectionToolbar, fontShadowToolbar, MENU_CONFIGS, TOOLBAR_ITEMS } from './config';
import { IGlowFeature } from '@framework/workbench/drawer/ui/text/effects/glow/glow-interface';
import { IReflectionFeature } from '@framework/workbench/drawer/ui/text/effects/reflection/reflection-interface';
import { IShadowFeature } from '@framework/workbench/drawer/ui/text/effects/shadow/shadow-interface';
import { BorderColorFeature } from '@framework/workbench/ui/border-color/border-color-feature';
import { BorderFeature, DEFAULT_SOLID_BORDER } from '@src/framework/workbench/drawer/ui/text/border/border-feature';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { CLICK_FROM } from '@framework/workbench/ui/constant';
import React from 'react';
import { wrapIdForCustomContent } from '@src/framework/workbench/util/wrap-id-util';
import { getAllChildModels } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import { isShapeOrConnector } from '@src/slide/domain/ui-model/elements/element-judge';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { isPreviewOnly } from '@src/page/toc/common/check-client-vars';

@Controller({
    toolbarItems: TOOLBAR_ITEMS,
    menuItems: MENU_CONFIGS,
})
export class ShapeTabController {
    public constructor(
        private readonly feature: ShapeTabFeature,
        private readonly borderFeature: BorderFeature,
        private readonly borderColorFeature: BorderColorFeature,
        @Loader(IGlowFeature) private glowLoader: IDynamicLoader<IGlowFeature>,
        @Loader(IReflectionFeature) private reflectionLoader: IDynamicLoader<IReflectionFeature>,
        @Loader(IShadowFeature) private shadowLoader: IDynamicLoader<IShadowFeature>,
    ) {}

    private get glowFeature() {
        return this.glowLoader.instance!;
    }

    private get reflectionFeature() {
        return this.reflectionLoader.instance!;
    }

    private get shadowFeature() {
        return this.shadowLoader.instance!;
    }

    @Toolbar(FIXED_KEYS_DOUBLE.TOOLBAR_SHAPE, 'shouldHide')
    public shouldHide() {
        const model = this.feature.getSelectModel() as IElementBaseModel;
        if (!model || isPreviewOnly()) return true;
        const models = getAllChildModels(model);
        return !models.filter(isShapeOrConnector).length;
    }

    @Toolbar(fontBorderToolbar.id, 'dropdownContent')
    public fontBorderToolbarDropdown() {
        return this.borderFeature.getBorderColorPickerNoKeep();
    }

    @Toolbar(fontBorderToolbar.id, 'onClick')
    public fontBorderToolbarOnClick() {
        const tdColor = this.borderFeature.getRecentColor()?.tdColor;
        return this.borderFeature.changeBorderColor(tdColor || new TDColor({
            rgbaStr: DEFAULT_SOLID_BORDER.color,
        }), CLICK_FROM.TOOLBAR);
    }

    @Toolbar(fontBorderToolbar.id, 'color')
    public fontBorderToolbarColor() {
        const tdColor = this.borderFeature.getRecentColor()?.tdColor;
        if (tdColor) {
            return tdColor.getColorWithNoAlpha();
        }

        return DEFAULT_SOLID_BORDER.color;
    }

    @Toolbar(fontBorderToolbar.id, 'shouldDisabled')
    public shouldDisabledTextBorder() {
        return this.feature.shouldDisabledTextBorder();
    }

    @Toolbar(fontEffectToolbar.id, 'dropdownContent')
    public fontEffectToolbarDropdown() {
        return <MainMenu
            align="left" name={fontEffectToolbar.id} mode="menu" itemStyle={{ width: 110, minWidth: 110 }} />;
    }

    @Menu(wrapIdForCustomContent(fontShadowToolbar.id), 'content')
    public fontShadowContent() {
        return this.shadowFeature.getPresetMenuContent({
            style: {
                border: 'none',
                padding: '8px 16px',
                height: 542,
            },
        });
    }

    @Menu(wrapIdForCustomContent(fontReflectionToolbar.id), 'content')
    public fontReflectionContent() {
        return this.reflectionFeature.getPresetMenuContent({
            style: {
                border: 'none',
                padding: '8px 16px',
                height: 106,
            },
        });
    }

    @Menu(wrapIdForCustomContent(fontGlowToolbar.id), 'content')
    public fontGlowContent() {
        return this.glowFeature.getPresetMenuContent({
            style: {
                border: 'none',
                padding: '8px 16px',
                height: 224,
            },
        });
    }
}
